import React, { useMemo, useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Typography, Tag } from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  ShopOutlined,
  SettingOutlined,
  BarChartOutlined,
  FileTextOutlined,
  LogoutOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  AuditOutlined,
  CreditCardOutlined,
  CalculatorOutlined,
  RocketOutlined,
  DatabaseOutlined,
  PhoneOutlined,
  BellOutlined,
  CarOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './SidebarStyles.css';

const { Sider } = Layout;
const { Text } = Typography;

function SuperAdminSidebar({ collapsed, onCollapse }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  const [openKeys, setOpenKeys] = useState([]);

  // Display name and avatar logic
  const displayName = useMemo(() => {
    const u = currentUser || {};
    return (
      u.display_name ||
      u.displayName ||
      (u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : null) ||
      u.name ||
      u.email?.split('@')[0] ||
      'Super Admin'
    );
  }, [currentUser]);

  const computedAvatar = useMemo(() => {
    const u = currentUser || {};
    return u.profile_picture_url || u.photoURL || u.avatar_url || u.image_url || null;
  }, [currentUser]);

  const [avatarUrl, setAvatarUrl] = useState(null);
  useEffect(() => {
    if (computedAvatar && /^\//.test(computedAvatar)) {
      setAvatarUrl(window.location.origin + computedAvatar);
    } else {
      setAvatarUrl(computedAvatar);
    }
  }, [computedAvatar]);

  const onOpenChange = (keys) => {
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = useMemo(() => [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
      path: '/dashboard/superadmin'
    },
    {
      key: 'all-agencies',
      icon: <ShopOutlined />,
      label: 'Enterprise',
      path: '/admin/agencies'
    },
    {
      key: 'whitelabel-agencies',
      icon: <ShopOutlined />,
      label: 'Resellers',
      path: '/admin/agencies/whitelabel'
    },
    {
      key: 'clients',
      icon: <TeamOutlined />,
      label: 'Clients',
      path: '/admin/clients'
    },
    {
      key: 'workspaces',
      icon: <FileTextOutlined />,
      label: 'Locations',
      path: '/admin/workspaces'
    },
    {
      key: 'vehicles-management',
      icon: <CarOutlined />,
      label: 'Vehicles Management',
      path: '/admin/vehicles-management'
    },
    {
      key: 'estimates',
      icon: <CalculatorOutlined />,
      label: 'Estimates',
      path: '/admin/estimates'
    },
    {
      key: 'create-campaign',
      icon: <RocketOutlined />,
      label: 'Create Campaign',
      path: '/admin/create-campaign'
    },
    {
      key: 'database',
      icon: <DatabaseOutlined />,
      label: 'Database Management',
      path: '/admin/database-management'
    },
    
    {
      key: 'billing',
      icon: <CreditCardOutlined />,
      label: 'Billing & Subscriptions',
      path: '/admin/billing'
    },
    {
      key: 'integrations',
      icon: <ApiOutlined />,
      label: 'Integrations Control',
      path: '/admin/integrations'
    },
    {
      key: 'platform-settings',
      icon: <SettingOutlined />,
      label: 'Platform Settings',
      path: '/admin/settings'
    },
    {
      key: 'user-management',
      icon: <UserOutlined />,
      label: 'User Management',
      path: '/admin/users'
    },
    {
      key: 'system-notifications',
      icon: <BellOutlined />,
      label: 'System Notifications',
      path: '/admin/system-notifications'
    },
    {
      key: 'analytics',
      icon: <BarChartOutlined />,
      label: 'Analytics & Reports',
      path: '/admin/analytics'
    },
    {
      key: 'audit-logs',
      icon: <AuditOutlined />,
      label: 'Audit Logs',
      path: '/admin/audit-logs'
    },
    {
      key: 'demo-requests',
      icon: <PhoneOutlined />,
      label: 'Demo Requests',
      path: '/admin/demo-requests'
    },
    {
      key: 'support',
      icon: <QuestionCircleOutlined />,
      label: 'Support & Tickets',
      path: '/admin/support'
    },
    {
      key: 'security',
      icon: <SafetyCertificateOutlined />,
      label: 'Security & Compliance',
      path: '/admin/security'
    }
  ], []);

  // Build a flattened items list for collapsed mode so every item (including children)
  // shows up as an icon with a tooltip.
  const flattenedItems = useMemo(() => {
    const flat = [];
    const walk = (items, parentIcon = null) => {
      items.forEach((it) => {
        const base = {
          key: it.key,
          icon: it.icon || parentIcon || <FileTextOutlined />,
          label: it.label,
          title: typeof it.label === 'string' ? it.label : undefined,
          path: it.path
        };
        if (it.children && it.children.length) {
          // include parent item itself if it has its own path
          if (it.path) flat.push(base);
          // include children with inherited icon
          walk(it.children, it.icon || parentIcon);
        } else {
          flat.push(base);
        }
      });
    };
    walk(menuItems);
    // Map to AntD Menu items format (no nesting)
    return flat.map(({ key, icon, label, title }) => ({ key, icon, label, title }));
  }, [menuItems]);

  const onMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else {
      const item = findMenuItemByKey(menuItems, key);
      if (item?.path) navigate(item.path);
    }
  };

  const findMenuItemByKey = (items, key) => {
    for (const item of items) {
      if (item.key === key) return item;
      if (item.children) {
        const found = findMenuItemByKey(item.children, key);
        if (found) return found;
      }
    }
    return null;
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      className="sidebar-container"
      width={250}
      collapsedWidth={80}
      trigger={null}
    >
      <div className="sidebar-header">
        <div className="logo-container" style={{ 
          padding: collapsed ? '12px 8px' : '16px', 
          textAlign: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}>
          <img 
            src="/SidebarLogo.png" 
            alt="PathMarkr Logo" 
            style={{ 
              height: 'auto',
              maxHeight: collapsed ? '40px' : '50px', 
              maxWidth: '100%', 
              width: 'auto',
              objectFit: 'contain',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            onClick={() => onCollapse(!collapsed)}
          />
          {!collapsed && (
            <div style={{ marginTop: '8px' }}>
              <Text strong style={{ fontSize: '14px', color: '#1f2937' }}>
                Pathmarkr Admin
              </Text>
            </div>
          )}
        </div>
      </div>

      {!collapsed && (
        <div
          style={{
            padding: '24px 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt="avatar"
              onError={() => { setAvatarUrl(null); }}
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '1px solid #f0f0f0', marginBottom: 8 }}
            />
          ) : (
            <Avatar
              size={80}
              style={{
                backgroundColor: '#ff4d4f',
                fontSize: '32px',
                fontWeight: 'bold',
                marginBottom: 8
              }}
            >
              {displayName
                .split(' ')
                .map((n) => n && n[0])
                .filter(Boolean)
                .join('')
                .toUpperCase()}
            </Avatar>
          )}
          <div style={{ marginTop: '8px', textAlign: 'center' }}>
            <Text strong style={{ display: 'block', color: 'var(--black)' }}>
              {displayName}
            </Text>
            {currentUser?.email && (
              <Text style={{ fontSize: '12px', color: 'var(--muted-gray)', display: 'block' }}>{currentUser.email}</Text>
            )}
            <Tag color="red" style={{ marginTop: '8px', fontSize: '11px' }}>
              SUPER ADMIN
            </Tag>
          </div>
        </div>
      )}

      <div className="sidebar-menu-wrapper">
        <Menu
          mode="inline"
          theme="light"
          inlineCollapsed={collapsed}
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={onOpenChange}
          onClick={onMenuClick}
          items={collapsed ? flattenedItems : menuItems}
          style={{
            borderRight: 0,
            background: 'transparent',
            marginTop: '16px'
          }}
          className="sidebar-menu"
        />
      </div>

      <div className="sidebar-footer" style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '8px 12px',
        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
        background: 'transparent'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Button
            type="text"
            className="sidebar-action sidebar-action--danger"
            icon={<LogoutOutlined />}
            onClick={handleLogout}
            danger
          >
            {!collapsed && 'Logout'}
          </Button>
        </div>
        {!collapsed && (
          <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--muted-gray)', whiteSpace: 'nowrap' }}>
            v1.4.7 Alpha | © 2026 PathMarkr
          </div>
        )}
      </div>
    </Sider>
  );
}

export default SuperAdminSidebar;
